<script setup lang="ts">
  import {
    MeterRegister,
    MoveInMeterReadingType,
    PublicMeterResponse,
  } from "@/api/model"
  import { DateFormats, formatDate } from "@/lib/dateHelper"
  import {
    isAllReadingsEmpty as isAllReadingsEmptyValidation,
    isEmptyReading,
    isValidReading,
  } from "@/lib/meter/meterReadingValidation"
  import getRegisterRateIcon from "@/lib/shared/getRegisterRateIcon"
  import { cn } from "@/lib/utils"
  import { MeterpointReadings } from "@/types/MeterpointReading"
  import { RateNames } from "@/types/RateNames"
  import { toTypedSchema } from "@vee-validate/zod"
  import { useVModel } from "@vueuse/core"
  import dayjs from "dayjs"
  import customParseFormat from "dayjs/plugin/customParseFormat"
  import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
  import { useForm } from "vee-validate"
  import { computed, onMounted, watch } from "vue"
  import { z, ZodRawShape } from "zod"
  import MeterReadInput from "@/components/shared/MeterReadInput.vue"
  import { Icon } from "@/components/ui/icons"
  import { DatePicker } from "@/components/ui/inputs/date-picker"

  dayjs.extend(customParseFormat)
  dayjs.extend(isSameOrAfter)
  type readingDateRecordKey = keyof Partial<
    Record<"readingDate", string | undefined>
  >

  const thresholdErrorMessage =
    "Your meter read should not be lower than your last"

  const props = defineProps<{
    modelValue?: MeterpointReadings
    meter: PublicMeterResponse
  }>()

  const emits = defineEmits<{
    "update:modelValue": [value: MeterpointReadings]
    updateDate: [value?: string]
  }>()

  const fuelType = computed<MoveInMeterReadingType>(() =>
    props.meter.isElectricity
      ? MoveInMeterReadingType.Electricity
      : MoveInMeterReadingType.Gas,
  )

  const modelValue = useVModel(props, "modelValue", emits, {
    passive: true,
  })

  onMounted(() => {
    modelValue.value = {
      meterpointId: props.meter.meterpointID,
      meterpointIdentifier: props.meter.meterpointIdentifier,
      readingDate: "",
      type: fuelType.value,
      readings:
        props.meter.registers?.map((register) => ({
          rateName: register.rateName,
          registerId: register.registerID,
          reading: [],
        })) || [],
      isValid: false,
      ...props.modelValue,
    }
  })

  const isStandardRegister = (register: MeterRegister) =>
    !register.rateName || register.rateName === RateNames.Standard

  const minDate = computed(() => {
    const lastReadingDate = props.meter.registers
      ?.filter((register) => {
        const readingDate = register.lastNonEstimatedReading?.readingDate

        if (!readingDate) return false

        return dayjs(readingDate).isValid()
      })
      .map((register) => register.lastNonEstimatedReading!.readingDate!)
      .sort((a, b) => dayjs(b).diff(dayjs(a)))[0]

    const fiveDaysAgo = dayjs().subtract(5, "days")

    if (lastReadingDate && dayjs(lastReadingDate).isAfter(fiveDaysAgo)) {
      return lastReadingDate
    }

    return fiveDaysAgo
  })
  const maxDate = dayjs().add(5, "days")

  const isAllReadingsEmpty = computed(() =>
    isAllReadingsEmptyValidation(
      props.meter,
      modelValue.value?.readings?.map((reading) => reading.reading),
    ),
  )

  const schema = z.object({
    readingDate: z
      .string()
      .optional()
      .refine(
        // date is required only if any meter is not empty
        (val) => (isAllReadingsEmpty.value ? true : !!val),
        { message: "Please enter a valid date" },
      )
      .refine(
        (val) =>
          !val ||
          dayjs(val, DateFormats.default).isSameOrAfter(minDate.value, "day"),
        {
          message: `Date must be no earlier than ${formatDate(minDate.value)}`,
        },
      )
      .refine(
        (val) =>
          !val || maxDate.isSameOrAfter(dayjs(val, DateFormats.default), "day"),
        { message: `Date must be no later than ${formatDate(maxDate)}` },
      ),
    ...(props.meter.registers ?? []).reduce<ZodRawShape>(
      (prev, current, index) => {
        return {
          ...prev,
          [`register_${current.registerID ?? index}`.toString()]: z
            .string()
            .optional()
            .array()
            .refine(
              (val) => {
                const isEmpty = isEmptyReading(val)

                return (
                  (isEmpty && isAllReadingsEmpty.value) ||
                  (!isEmpty && isValidReading(val, current.digitCount))
                )
              },
              { message: "Please enter a valid read" },
            )
            .refine(
              (val) =>
                !val.length ||
                !current.lastNonEstimatedReading?.value ||
                isEmptyReading(val) ||
                current.lastNonEstimatedReading.value < parseInt(val.join("")),
              { message: thresholdErrorMessage },
            ),
        }
      },
      {},
    ),
  })

  const formSchema = toTypedSchema(schema)

  const { errors, meta, validate } = useForm({
    validationSchema: formSchema,
  })

  const isAllReadingsOverThreshold = computed(() =>
    Object.values(errors.value).every(
      (error) => error !== thresholdErrorMessage,
    ),
  )

  const updateDate = (val?: string) => {
    modelValue.value = {
      ...modelValue.value!,
      readingDate: val || "",
    }
    emits("updateDate", val)
  }

  const updateReading = (val: string[], index: number) => {
    modelValue.value = {
      ...modelValue.value!,
      readings: modelValue.value!.readings.map((reading, modelIndex) => {
        if (index === modelIndex) {
          return {
            ...reading,
            reading: val,
          }
        }
        return reading
      }),
    }
  }

  watch(
    () => meta.value.valid,
    (val) => {
      modelValue.value = {
        ...modelValue.value!,
        isValid: !isAllReadingsEmpty.value && val,
      }
    },
  )

  defineExpose({
    formMeta: meta,
    validate,
    isAllReadingsOverThreshold,
  })
</script>

<template>
  <div>
    <h4 class="mb-2 flex items-center gap-2">
      <Icon :name="fuelType" size="lg" />
      {{ `${meter.isElectricity ? "Electricity" : "Gas"} Reading` }}
    </h4>
    <p class="mb-6 sm:mb-4">
      <span class="body-text-strong">Meter Serial Number:</span>
      {{ meter.meterpointIdentifier }}
    </p>

    <form novalidate>
      <div
        :class="
          cn(
            'flex flex-wrap gap-6 sm:flex-nowrap',
            (meter.registers?.length || 1) > 1 && 'sm:flex-wrap',
          )
        "
      >
        <DatePicker
          :model-value="modelValue?.readingDate"
          data-testid="meter-read-date"
          :validate-on-model-update="true"
          name="readingDate"
          label="Reading date"
          :class="cn('min-w-[187px] max-w-[320px] basis-full')"
          @update:model-value="updateDate($event)"
        />

        <div
          :class="
            cn(
              'grid grid-cols-1 gap-6',
              (meter.registers?.length || 1) > 1 &&
                'col-span-2 sm:grid-cols-[minmax(187px,320px)_minmax(187px,500px)]',
            )
          "
        >
          <MeterReadInput
            v-for="(register, index) in meter.registers || []"
            :key="register.registerID || `register_${index}`"
            :model-value="modelValue?.readings?.[index].reading ?? []"
            :class="
              cn(
                (meter.registers?.length || 1) > 1 &&
                  index === 0 &&
                  'min-w-[187px] max-w-[320px]',
                (meter.registers?.length || 1) > 1 && index !== 0 && '',
              )
            "
            :name="`register_${register.registerID ?? index}`.toString()"
            :input-length="register.digitCount ?? 1"
            :label="`New ${!isStandardRegister(register) ? register.rateName : ''} ${fuelType.toLowerCase()} reading`"
            :label-icon="
              !isStandardRegister(register)
                ? getRegisterRateIcon(register.rateName)
                : undefined
            "
            :disabled="!!errors['readingDate']"
            show-red-digit
            required
            :has-clear-btn="
              !!errors[
                `register_${register.registerID ?? index}` as readingDateRecordKey
              ]
            "
            @update:model-value="updateReading($event, index)"
          />
        </div>
      </div>
    </form>
  </div>
</template>
